import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";
import { useTranslation } from "../i18n";
import { toast } from "react-toastify";
import { playError, playSuccess } from "../components/helpers/audioPlayer";
import {
    preventScanPageButtonFocus,
    blurClickedScanPageButton,
} from "../components/helpers/scanFocusGuard";
import HiddenScanInput from "../components/HiddenScanInput";
import FitWord from "../components/FitWord";
import ScanStatusIcons from "../components/ScanStatusIcons";
import ScanFlashOverlay from "../components/ScanFlashOverlay";
import PrinterSelectModal from "../components/PrinterSelectModal";
import {
    silentPrintBarcode,
    isPrintAgentAvailable,
} from "../components/helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
    isSavedNetworkPrinterOnline,
} from "../components/helpers/networkPrint";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { apiFetch } from "../api/apiFetch";
import "../styles/scansheet.css";

// Relabel — Goods In scan-and-print station.
//
// An operative scans an item's ORIGINAL barcode; the BE dispenses the next
// unused NEW barcode assigned to it (FIFO, marked 'used') and we print it on
// the same 65x38mm CODE128 label the scan sheet uses (buildBarcodeLabel via
// printBarcode). Scan the same original again → the next new barcode prints,
// until they run out. The print machinery here mirrors scansheet.tsx.
const Relabel = () => {
    usePageTitle("relabel");
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [scanValue, setScanValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error">("idle");
    const [lastNewBarcode, setLastNewBarcode] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Print state — supports local (print agent) and network (backend relay)
    // modes, restored from localStorage. Mirrors the scan sheet's behaviour so
    // the same printer the operator already paired works here unchanged.
    const [autoPrint, setAutoPrint] = useState(false);
    const [printChecking, setPrintChecking] = useState(() => localStorage.getItem("relabel-auto-print") === "true");
    const [printMode, _setPrintMode] = useState<"local" | "network" | null>(null);
    const [printerOnline, setPrinterOnline] = useState(true);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const [printFlash, setPrintFlash] = useState(false);
    const printModeRef = useRef<"local" | "network" | null>(null);
    useEffect(() => { printModeRef.current = printMode; }, [printMode]);

    const setPrintMode = (mode: "local" | "network" | null) => {
        _setPrintMode(mode);
        if (mode) localStorage.setItem("relabel-print-mode", mode);
        else localStorage.removeItem("relabel-print-mode");
    };

    // On mount, restore saved printer preference and verify it's online.
    useEffect(() => {
        const saved = localStorage.getItem("relabel-auto-print") === "true";
        const savedMode = localStorage.getItem("relabel-print-mode");
        if (!saved) { setPrintChecking(false); return; }

        const connectIfOnline = (online: boolean, mode: "local" | "network") => {
            if (online) {
                setAutoPrint(true);
                setPrintMode(mode);
                setPrinterOnline(true);
            } else {
                setAutoPrint(false);
                _setPrintMode(null);
                setPrinterOnline(false);
            }
            setPrintChecking(false);
        };

        if (savedMode === "network" && getSavedNetworkPrinter()) {
            isSavedNetworkPrinterOnline().then(online => connectIfOnline(online, "network"));
        } else if (savedMode === "local") {
            isPrintAgentAvailable().then(online => connectIfOnline(online, "local"));
        } else {
            isPrintAgentAvailable().then(available => {
                if (available) {
                    connectIfOnline(true, "local");
                } else if (getSavedNetworkPrinter()) {
                    isSavedNetworkPrinterOnline().then(online => connectIfOnline(online, "network"));
                } else {
                    setAutoPrint(false);
                    setPrinterOnline(false);
                    setPrintChecking(false);
                }
            });
        }
    }, []);

    // Periodically re-check the selected printer (every 30s) so the icon
    // reflects reality and we don't try to print to a dead printer.
    useEffect(() => {
        if (!autoPrint || !printMode) return;
        const checkOnline = async () => {
            const online = printMode === "local"
                ? await isPrintAgentAvailable()
                : await isSavedNetworkPrinterOnline();
            setPrinterOnline(online);
            if (!online) {
                setAutoPrint(false);
                _setPrintMode(null);
            }
        };
        const interval = setInterval(checkOnline, 30_000);
        return () => clearInterval(interval);
    }, [autoPrint, printMode]);

    useEffect(() => () => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    }, []);

    const flashStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        statusTimerRef.current = setTimeout(() => setScanStatus("idle"), 800);
    };

    // Print a barcode label — local (print agent) or network (backend relay).
    // Reuses buildBarcodeLabel so the label is byte-for-byte the scan sheet's
    // 65x38mm CODE128 label, and passes the raw value for the Zebra ZPL
    // fast-path. Identical to scansheet.tsx printBarcode.
    const printBarcode = useCallback(async (barcodeValue: string) => {
        setPrintFlash(true);
        setTimeout(() => setPrintFlash(false), 500);

        const useNetwork = printModeRef.current === "network" || (!printModeRef.current && getSavedNetworkPrinter());
        if (useNetwork) {
            const saved = getSavedNetworkPrinter();
            if (saved) {
                const { buildBarcodeLabel } = await import("../components/helpers/printAgent");
                const result = await submitNetworkPrintJob(
                    saved.agentId,
                    saved.printerName,
                    buildBarcodeLabel(barcodeValue),
                    1,
                    barcodeValue,
                );
                if (!result.success) {
                    toast.dismiss();
                    toast.error(result.error || t("print_job_failed"));
                }
                return;
            }
        }

        const result = await silentPrintBarcode(barcodeValue);
        if (!result.success) {
            toast.dismiss();
            toast.error(result.error || t("print_failed"));
        }
    }, [t]);

    const doSubmit = async (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        setScanValue("");
        if (!val) return;

        setLoading(true);
        try {
            const res = await apiFetch(`/relabel/dispense`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: val }),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            if (!res.ok) {
                playError();
                flashStatus("error");
                setMessage(t("relabel_dispense_failed"));
                return;
            }
            const data = await res.json();
            if (data.outcome === "ok") {
                setLastNewBarcode(data.newBarcode);
                setMessage(t("relabel_remaining", { count: data.remaining }));
                flashStatus("success");
                playSuccess();
                printBarcode(data.newBarcode);
            } else if (data.outcome === "all_used") {
                playError();
                flashStatus("error");
                setLastNewBarcode(null);
                setMessage(t("relabel_all_used"));
            } else {
                // not_found
                playError();
                flashStatus("error");
                setLastNewBarcode(null);
                setMessage(t("relabel_not_found"));
            }
        } catch {
            playError();
            flashStatus("error");
            setMessage(t("relabel_dispense_failed"));
        } finally {
            setLoading(false);
        }
    };

    const commandWords = t("relabel_scan_original").toUpperCase().split(/[\s-]+/).filter(Boolean);

    return (
        <div
            className="scan-panel"
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("relabel")}</h1>
                <button className="page-back-btn" onClick={() => navigate("/goods-in")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            <div className="scan-centre">
                <div className="scan-command-wrap">
                    {!lastNewBarcode && <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />}
                    <div className={`scan-command ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                        {commandWords.map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                </div>

                <div className={`scan-feedback-area ${scanStatus === "error" ? "scan-command-hidden" : ""}`}>
                    {lastNewBarcode && (
                        <div className="scan-feedback">
                            <p className="scan-feedback-id scan-pkgid">{lastNewBarcode}</p>
                        </div>
                    )}
                    {message && (
                        <div className="scan-command-sub">{message}</div>
                    )}
                </div>
            </div>

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={loading}
                printConnected={printChecking ? undefined : (autoPrint && printerOnline)}
                printFlash={printFlash}
                onPrintClick={() => setShowPrinterSelect(true)}
                canReprint={!!lastNewBarcode}
                onReprintClick={() => { if (lastNewBarcode) printBarcode(lastNewBarcode); }}
                enableTntMode
            />

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={autoPrint}
                    printMode={printMode}
                    onSelect={() => {
                        setShowPrinterSelect(false);
                        setAutoPrint(true);
                        setPrintMode("network");
                        setPrinterOnline(true);
                        localStorage.setItem("relabel-auto-print", "true");
                    }}
                    onConnectLocal={() => {
                        setShowPrinterSelect(false);
                        setAutoPrint(true);
                        setPrintMode("local");
                        setPrinterOnline(true);
                        localStorage.setItem("relabel-auto-print", "true");
                    }}
                    onDisconnect={() => {
                        setShowPrinterSelect(false);
                        setAutoPrint(false);
                        setPrintMode(null);
                        setPrinterOnline(false);
                        localStorage.setItem("relabel-auto-print", "false");
                    }}
                    onClose={() => setShowPrinterSelect(false)}
                />
            )}
        </div>
    );
};

export default Relabel;
